import { useState, useEffect } from "react";
import { History, ArrowRight, Clock, CheckCircle2, Archive } from "lucide-react";
import { apiRequest } from "../context/AuthContext";

/**
 * Standalone Year Switch Log timeline — shows ALL switches (including reverted)
 * for the given userId. Used on the Profile page so players don't have to scroll
 * to the bottom of the Dashboard to see their switch history.
 *
 * Returns null when the player has no switches at all.
 */
export default function SwitchHistoryLog({ userId }) {
  const [switches, setSwitches] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!userId) return;
    apiRequest("get", `/players/${userId}/enrolment-year-switches`)
      .then(({ data }) => setSwitches(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading || switches.length === 0) return null;

  const fmtDate = iso =>
    iso
      ? new Date(iso).toLocaleDateString("en-GB", {
          day: "numeric", month: "short", year: "numeric",
        })
      : "—";

  const statusBadge = s => {
    if (s.reverted) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Reverted
        </span>
      );
    }
    if (s.superseded) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
          <Archive className="w-3 h-3" /> Superseded
        </span>
      );
    }
    if (s.revert_eligible) {
      const daysLeft = Math.max(0, Math.ceil(
        (new Date(s.revert_deadline) - Date.now()) / 86_400_000
      ));
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
          <Clock className="w-3 h-3" /> {daysLeft}d to undo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
        <Archive className="w-3 h-3" /> Permanent
      </span>
    );
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden"
      data-testid="switch-history-log"
    >
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
        <History className="w-4 h-4 text-slate-400" />
        <h3
          className="font-bold text-sm tracking-wide"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}
        >
          Year Switch Log
        </h3>
        <span className="ml-auto text-xs text-slate-400">
          {switches.length} switch{switches.length !== 1 ? "es" : ""}
        </span>
      </div>

      <div className="divide-y divide-slate-50">
        {switches.map(s => (
          <div
            key={s.id}
            data-testid={`switch-log-row-${s.id}`}
            className="flex items-center gap-4 px-5 py-3.5"
          >
            {/* Timeline dot */}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              s.reverted ? "bg-green-400" : s.revert_eligible ? "bg-orange-400" : "bg-slate-300"
            }`} />

            {/* Direction */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-sm font-bold text-slate-700">{s.old_target_year}</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-sm font-bold text-slate-700">{s.new_target_year}</span>
            </div>

            {/* Date */}
            <span className="text-xs text-slate-400 flex-1">{fmtDate(s.switched_at)}</span>

            {/* Status + revert date */}
            <div className="flex flex-col items-end gap-0.5">
              {statusBadge(s)}
              {s.reverted && s.reverted_at && (
                <span className="text-xs text-slate-400">{fmtDate(s.reverted_at)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
